/**
 * Stage 1: Core Identity Component
 *
 * First stage of guide signup flow - collects essential identity information
 * Extracted from GuideSignupFlow.tsx mockup
 *
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/components/guides/signup/Stage1CoreIdentity.jsx
 */

import React, { useState, useEffect } from 'react';
import {
  User as _User,
  Mail as _Mail,
  Lock,
  Phone,
  MapPin as _MapPin,
  Award,
  Shield,
  Camera,
  Eye,
  EyeOff,
  HelpCircle,
  AlertCircle as _AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Check
} from 'lucide-react';
import { validateStage1 } from '../../../utils/guideValidation';
import { useAuth } from '../../../contexts/AuthContext';
import SmsConsentCheckbox from './SmsConsentCheckbox';
import GoogleLoginButton from '../../auth/GoogleLoginButton';
import apiRequest from '../../../services/api';

/**
 * Stage1CoreIdentity Component
 *
 * @param {Object} props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.onUpdate - Handler for form updates
 * @param {Function} props.onNext - Handler for "Continue" button
 * @param {Function} props.onBack - Handler for "Back" button
 * @param {boolean} props.isOAuth - Whether user is signing up with OAuth
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element}
 */
const Stage1CoreIdentity = ({
  formData = {},
  onUpdate,
  onNext,
  onBack,
  isOAuth: _isOAuth = false,
  className = ''
}) => {
  const { user, isAuthenticated, setTokenFromCallback } = useAuth();

  const [errors, setErrors] = useState({});
  const [_touched, _setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isAutoSaving, _setIsAutoSaving] = useState(false);
  const [_lastSaved, _setLastSaved] = useState(null);

  // 'local' (Scenario C default, Scenario A) or 'google' (Scenario B)
  const [authProvider, setAuthProvider] = useState('local');
  const smsConsentGranted = formData.smsConsentGranted ?? false;
  const [googleToken, setGoogleToken] = useState(null);
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scenario A: existing platform session — pre-fill from user object, lock email
  useEffect(() => {
    if (isAuthenticated && user && onUpdate) {
      onUpdate({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
      setIsEmailLocked(true);
    }
  // onUpdate is stable per parent render; eslint-disable covers stale-closure false positive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  /**
   * Calculate password strength
   */
  const calculatePasswordStrength = (password) => {
    if (!password) {return { score: 0, label: 'None', color: 'bg-gray-200' };}

    let score = 0;

    // Length
    if (password.length >= 8) {score++;}
    if (password.length >= 12) {score++;}

    // Character types
    if (/[a-z]/.test(password)) {score++;}
    if (/[A-Z]/.test(password)) {score++;}
    if (/[0-9]/.test(password)) {score++;}
    if (/[^a-zA-Z0-9]/.test(password)) {score++;}

    // Cap at 5
    score = Math.min(score, 5);

    const labels = ['None', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['bg-gray-200', 'bg-red-500', 'bg-yellow-500', 'bg-yellow-500', 'bg-emerald-500', 'bg-emerald-600'];

    return {
      score,
      // eslint-disable-next-line security/detect-object-injection -- safe: score is controlled integer 0-5
      label: labels[score],
      // eslint-disable-next-line security/detect-object-injection -- safe: score is controlled integer 0-5
      color: colors[score]
    };
  };

  const passwordStrength = calculatePasswordStrength(formData.password || '');

  /**
   * Format phone number
   */
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    let cleaned = value.replace(/\D/g, '');

    // Strip leading 1 (US country code) if present and there are 11 digits
    if (cleaned.length === 11 && cleaned[0] === '1') {
      cleaned = cleaned.substring(1);
    }

    // Only keep first 10 digits
    cleaned = cleaned.substring(0, 10);

    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) {return value;}

    const parts = [];
    if (match[1]) {parts.push(`(${match[1]}`);}
    if (match[2]) {parts.push(`) ${match[2]}`);}
    if (match[3]) {parts.push(`-${match[3]}`);}

    return parts.join('');
  };

  /**
   * Handle field change
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const handleChange = (field, value) => {
    if (onUpdate) {
      onUpdate({ [field]: value });
    }

    // Mark field as touched
    // eslint-disable-next-line security/detect-object-injection -- safe: field is validated form field name
    _setTouched((prev) => ({ ...prev, [field]: true }));

    // Clear error for this field
    // eslint-disable-next-line security/detect-object-injection -- safe: field is validated form field name
    if (errors[field] || (field === 'email' && errors.emailAlreadyExists)) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        // eslint-disable-next-line security/detect-object-injection -- safe: field is validated form field name
        delete newErrors[field];
        if (field === 'email') { delete newErrors.emailAlreadyExists; }
        return newErrors;
      });
    }
  };

  /**
   * Handle phone change with formatting
   */
  const handlePhoneChange = (_e) => {
    const formatted = formatPhoneNumber(_e.target.value);
    handleChange('phone', formatted);
  };

  /**
   * Handle profile photo selection
   */
  const handlePhotoSelect = (_e) => {
    if (_e.target.files && _e.target.files[0]) {
      handleChange('profilePhoto', _e.target.files[0]);
    }
  };

  // Scenario B: Google OAuth callback — decode JWT, pre-fill fields, lock email
  const handleGoogleSuccess = (credentialResponse) => {
    const { credential } = credentialResponse;
    try {
      const payload = JSON.parse(atob(credential.split('.')[1]));
      const { given_name: firstName, family_name: lastName, email } = payload;
      if (onUpdate) {
        onUpdate({
          firstName: firstName || '',
          lastName: lastName || '',
          email: email || '',
        });
      }
    } catch {
      // Decode failure — fields stay empty, user can fill manually
    }
    setIsEmailLocked(true);
    setAuthProvider('google');
    setGoogleToken(credential);
  };

  /**
   * Handle form submission — calls POST /api/v1/guides/signup/start
   * @param {Event} _e - Form event
   */
  const handleSubmit = async (_e) => {
    _e.preventDefault();

    if (isSubmitting) {return;}

    setSubmitError(null);

    // Client-side: confirm password match before hitting server
    if (authProvider !== 'google' && formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' })); // gitleaks:allow
      return;
    }

    const validation = validateStage1({
      ...formData,
      isOAuth: authProvider === 'google',
    });

    if (!validation.isValid) {
      setErrors(validation.errors);
      const touchedFields = {};
      Object.keys(validation.errors).forEach((field) => {
        // eslint-disable-next-line security/detect-object-injection -- safe: field is a known validation error key from validateStage1
        touchedFields[field] = true;
      });
      _setTouched(touchedFields);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        smsConsentGranted,
        authProvider,
      };

      if (authProvider !== 'google') {
        payload.password = formData.password; // gitleaks:allow
        payload.confirmPassword = formData.confirmPassword; // gitleaks:allow
      }

      if (authProvider === 'google' && googleToken) {
        payload.oauthCode = googleToken;
      }

      const response = await apiRequest('/api/v1/guides/signup/start', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response?.accessToken) {
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('rememberMe', 'true');
        }
        localStorage.setItem('tokenIssuedAt', String(Date.now()));
        await setTokenFromCallback(response.accessToken);
      }

      if (response?.guideId && onUpdate) {
        onUpdate({ guideId: response.guideId });
      }

      if (onNext) {
        onNext();
      }
    } catch (err) {
      if (err?.error === 'EMAIL_ALREADY_EXISTS') {
        setErrors((prev) => ({ ...prev, emailAlreadyExists: true }));
        setIsEmailLocked(false);
      } else if (err?.error === 'PASSWORD_MISMATCH') {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' })); // gitleaks:allow
      } else {
        setSubmitError(err?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`max-w-2xl mx-auto px-4 py-8 ${className}`}>
      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>Step 1 of 5</span>
          <span className="flex items-center">
            {isAutoSaving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-emerald-600 border-t-transparent mr-1.5"></div>
                Saving...
              </>
            ) : _lastSaved ? (
              <>
                <Check className="w-3 h-3 text-emerald-600 mr-1.5" />
                Saved {new Date(_lastSaved).toLocaleTimeString()}
              </>
            ) : null}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
            style={{ width: '33%' }}
          />
        </div>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Let&apos;s Get You Started
        </h2>
        <p className="text-gray-600">
          Basic info to create your account. Takes about 7-8 minutes.
        </p>
      </div>

      {/* Social Login Options — hidden once Google OAuth completes (Scenario B) or session detected (Scenario A) */}
      {!isEmailLocked && (
        <div className="mb-8">
          <div className="flex justify-center mb-4">
            <GoogleLoginButton
              onSuccess={handleGoogleSuccess}
              onError={() => setSubmitError('Google sign-in failed. Please try again.')}
            />
          </div>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* Profile Photo Upload */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-200">
          <div className="flex flex-col items-center text-center">
            <Camera className="w-5 h-5 text-emerald-600 mb-2" />
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              Add a profile photo (optional)
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                +40% bookings
              </span>
            </h4>
            <p className="text-xs text-gray-600 mb-3">
              Profiles with photos get significantly more bookings. You can always add this later.
            </p>
            <label className="inline-flex items-center px-4 py-2 bg-white border-2 border-emerald-600 text-emerald-600 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors">
              <Camera className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Choose Photo</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />
            </label>
            {formData.profilePhoto && (
              <div className="mt-2 flex items-center text-sm text-emerald-600">
                <CheckCircle className="w-4 h-4 mr-1" />
                Photo selected: {formData.profilePhoto.name}
              </div>
            )}
          </div>
        </div>

        {/* Name Fields - Side by Side on Desktop */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
              First Name
              <button
                type="button"
                className="ml-1 text-gray-400 hover:text-gray-600"
                title="Your legal first name for verification purposes"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </label>
            <div className="relative">
              <_User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="e.g., Jake"
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.firstName && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <_AlertCircle className="w-4 h-4 mr-1" />
                {errors.firstName}
              </p>
            )}
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
              Last Name
              <button
                type="button"
                className="ml-1 text-gray-400 hover:text-gray-600"
                title="Your legal last name for verification purposes"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </label>
            <input
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
              placeholder="e.g., Morrison"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                errors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.lastName && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <_AlertCircle className="w-4 h-4 mr-1" />
                {errors.lastName}
              </p>
            )}
          </div>
        </div>

        {/* Business Name */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
            Business/Guide Service Name
            <button
              type="button"
              className="ml-1 text-gray-400 hover:text-gray-600"
              title="Your business name as registered with your state. Used for license verification and your public profile."
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </label>
          <div className="relative">
            <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={formData.businessName || ''}
              onChange={(e) => handleChange('businessName', e.target.value)}
              placeholder="e.g., Summit Adventures LLC"
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                errors.businessName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.businessName && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.businessName}
            </p>
          )}
          <p className="mt-1.5 text-xs text-gray-500 text-center">
            Your registered business name—required for state licensing verification
          </p>
        </div>

        {/* Why We Need Business Name - Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Why we verify your business</h4>
              <p className="text-xs text-gray-700 mb-2">
                We use your business name to verify state licenses, permits, and registrations. This ensures:
              </p>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Your business is properly licensed in your operating state</li>
                <li>• Required permits and insurance are current</li>
                <li>• Customers can trust you&apos;re a legitimate, registered guide service</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
            Email Address
            {!isEmailLocked && (
              <button
                type="button"
                className="ml-1 text-gray-400 hover:text-gray-600"
                title="Used for booking notifications, customer inquiries, and account recovery"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            )}
          </label>
          <div className="relative">
            {isEmailLocked
              ? <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              : <_Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            }
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="jake.morrison@example.com"
              autoComplete="email"
              disabled={isEmailLocked}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                errors.email || errors.emailAlreadyExists ? 'border-red-500' : 'border-gray-300'
              } ${isEmailLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>
          {isEmailLocked && (
            <p className="mt-1.5 text-xs text-gray-500 text-center">
              Email from your existing account
            </p>
          )}
          {errors.email && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.email}
            </p>
          )}
          {errors.emailAlreadyExists && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              An account with this email already exists.{' '}
              <a href="/login" className="underline ml-1 hover:text-red-800">Sign in instead?</a>
            </p>
          )}
          {!isEmailLocked && !errors.email && !errors.emailAlreadyExists && (
            <p className="mt-1.5 text-xs text-gray-500 text-center">
              We&apos;ll send a verification code to confirm your email
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
            Phone Number
            <button
              type="button"
              className="ml-1 text-gray-400 hover:text-gray-600"
              title="For SMS booking alerts and customer contact. We never share your number."
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              autoComplete="tel"
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.phone && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.phone}
            </p>
          )}

          <div className="mt-3">
            <SmsConsentCheckbox
              checked={smsConsentGranted}
              onChange={(e) => {
                onUpdate({ smsConsentGranted: e.target.checked });
              }}
            />
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
            Primary Location
            <button
              type="button"
              className="ml-1 text-gray-400 hover:text-gray-600"
              title="Your base location helps us match you with nearby customers"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </label>
          <div className="relative">
            <_MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="e.g., Denver, Colorado"
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                errors.location ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.location && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.location}
            </p>
          )}
          <p className="mt-1.5 text-xs text-gray-500 text-center">
            Where you&apos;re based—helps match you with local customers
          </p>
        </div>

        {/* Password + Confirm Password — hidden for Scenario B (Google OAuth) */}
        {authProvider !== 'google' && (
          <>
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
                Password
                <button
                  type="button"
                  className="ml-1 text-gray-400 hover:text-gray-600"
                  title="Choose a strong password with at least 8 characters, including uppercase, lowercase, numbers, and symbols"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password || ''}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password" // gitleaks:allow
                  className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-gray-600 hover:text-gray-900"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center">
                  <_AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password}
                </p>
              )}

              {/* Password Strength Indicator — gitleaks:allow */}
              {formData.password && formData.password.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password strength:</span>{/* gitleaks:allow */}
                    <span className={`text-xs font-medium ${
                      passwordStrength.score < 2 ? 'text-red-600' :
                      passwordStrength.score < 4 ? 'text-yellow-600' :
                      'text-emerald-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          level <= passwordStrength.score ? passwordStrength.color : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  {passwordStrength.score < 4 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Tip: Use uppercase, lowercase, numbers, and symbols
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
                Confirm Password{/* gitleaks:allow */}
              </label>
              <input
                type="password" // gitleaks:allow
                value={formData.confirmPassword || ''}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.confirmPassword && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center">
                  <_AlertCircle className="w-4 h-4 mr-1" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </>
        )}

        {/* Terms & Conditions */}
        <div>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={formData.termsAccepted || false}
              onChange={(e) => handleChange('termsAccepted', e.target.checked)}
              className={`mt-0.5 w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 ${
                errors.termsAccepted ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <span className="ml-2 text-sm text-gray-600">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline">
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.termsAccepted && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.termsAccepted}
            </p>
          )}
        </div>

        {/* Form-level error */}
        {submitError && (
          <p className="text-sm text-red-600 flex items-center justify-center">
            <_AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
            {submitError}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4">
          <button
            type="button"
            onClick={() => {
              if (onBack) {
                onBack();
              }
            }}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Continue'}
            {!isSubmitting && <ChevronRight className="ml-2 w-4 h-4" />}
          </button>
        </div>
      </form>

      {/* Trust Signal */}
      <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-center text-sm text-gray-600">
        <Shield className="w-4 h-4 mr-1.5 text-emerald-600" />
        Your data is encrypted and never shared without permission
      </div>
    </div>
  );
};

export default Stage1CoreIdentity;
